import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatDate, sectionLabels } from "@/lib/content";

export default function HeroCarousel({ items }: { items: Article[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-lg bg-primary-950">
      {items.map((item, i) => (
        <Link
          key={item.id}
          to={`/detail/${item.id}`}
          className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title}头图`}
            title={`${item.brand}${item.title}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/45 to-primary-950/15" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 pb-8 md:pb-12">
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 font-semibold mb-3">
                <i className="ri-fire-fill" />
                本周头条 · {sectionLabels[item.section]}
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-background-50 max-w-3xl leading-snug line-clamp-2">
                {item.title}
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-200 max-w-2xl line-clamp-2">
                {item.summary}
              </p>
              <div className="mt-4 flex items-center gap-4 text-xs md:text-sm text-background-300">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  {item.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {formatDate(item.date)}
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={(e) => {
          e.preventDefault();
          go(index - 1);
        }}
        className="absolute z-20 left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/85 hover:bg-background-50 flex items-center justify-center cursor-pointer text-primary-900"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={(e) => {
          e.preventDefault();
          go(index + 1);
        }}
        className="absolute z-20 right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-background-50/85 hover:bg-background-50 flex items-center justify-center cursor-pointer text-primary-900"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute z-20 bottom-4 right-6 md:right-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`第${i + 1}张`}
            onClick={(e) => {
              e.preventDefault();
              setIndex(i);
            }}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
}